import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, firstValueFrom, from, map, Observable, tap, throwError } from 'rxjs';
import { PaginatedResult, SimpleMessage, SsoApiResponse } from '../model/shared';
import { UserApp } from '../model/sso-client';
import { SearchUserCriteria, SsoUser, UserProfile } from '../model/sso-user';
import { convertSnapshotsToData, formatApiUrl } from './utilities';
import Typesense, { Client } from 'typesense';
import { ConfigService } from './config.service';
import { NotificationClient, NotificationSettingsDto } from 'functions/src/models/shared/notificationSettings.model';
import { FirestoreService } from '../shared/services/firestore.service';
import { Auth, signInWithEmailAndPassword } from '@angular/fire/auth';
import { limit, orderBy, OrderByDirection, startAfter, where } from '@angular/fire/firestore';

@Injectable({
  providedIn: 'root'
})
export class UserAdminService {

  private fireAuth: Auth = inject(Auth);

  _typesenseClient: Client;
  async getTypesenseClient(): Promise<Client> {

    if (!this._typesenseClient) {
      // Load the object here, e.g. from an API call
      const adminConfig = await firstValueFrom(this.configService.getAdminConfig());
      this._typesenseClient = new Typesense.Client({
        'nodes': [{
          'host': adminConfig.typesense.host,
          'port': 443,
          'protocol': 'https'
        }],
        'apiKey': adminConfig.typesense.usersSearch.apiKey,
        'connectionTimeoutSeconds': 30
      })
    }

    return this._typesenseClient;
  }

  searchPrefix = {
    clientId: "cI_",
    role: "r_",
    clientIdRole: "cIr_"
  }

  constructor(
    private http: HttpClient,
    private db: FirestoreService,
    private configService: ConfigService
  ) {
    this.init();
  }
  async init() {

  }

  generateUID(): string {
    return this.db.createDocumentId('users');
  }

  getUsers(filters: any): Observable<PaginatedResult<SsoUser>> {

    const query = [];

    if (filters.emailVerifiedFilter !== null) {
      query.push(where("emailVerified", "==", filters.emailVerifiedFilter));

    }
    else if (filters.firstnameFilter !== null) {
      const startsWith: string = filters.firstnameFilter;
      //starts with
      query.push(orderBy("firstname"));
      query.push(where("firstname", ">=", startsWith));
      query.push(where("firstname", "<", startsWith + 'z'));
    }
    else if (filters.lastnameFilter !== null) {
      const startsWith: string = filters.lastnameFilter;
      //starts with
      query.push(orderBy("lastname"));
      query.push(where("lastname", ">=", startsWith));
      query.push(where("lastname", "<", startsWith + 'z'));
    }
    else if (filters.emailFilter !== null) {
      const email: string = filters.emailFilter;
      //starts with
      query.push(where("email", "==", email));
    }
    else {
      query.push(orderBy("email"));
    }

    const data = from(this.db.getCollectionSnapshots('users', query))

    return data.pipe(
      map(results => {
        const paginatedResult = {
          data: convertSnapshotsToData<SsoUser>(results),
          lastItem: results.docs[results.docs.length-1],
        } as PaginatedResult<SsoUser>;

        return paginatedResult;
      })
    )
  }

  searchUsers(criteria: SearchUserCriteria): Observable<PaginatedResult<SsoUser>> {
    const query = [];

    if (criteria.searchTerm ||
      criteria.clientId ||
      criteria.roleName) {
      let searchToken: string;
      if (criteria.clientId && criteria.roleName) {
        searchToken = this.formatSearchToken(criteria.searchTerm, "", "", `${criteria.clientId}_${criteria.roleName}`)
      } else {
        searchToken = this.formatSearchToken(criteria.searchTerm, criteria.clientId, criteria.roleName, "")
      }

      query.push(where("searchTokens", "array-contains", searchToken));
    }

    if (criteria.emailVerified) {
      query.push(where("emailVerified", "==", criteria.emailVerified == "true"))
    }

    if (criteria.disabled) {
      query.push(where("disabled", "==", criteria.disabled == "true"))
    }

    if (criteria.isSsoAdmin) {
      query.push(where("isSsoAdmin", "==", criteria.isSsoAdmin == "true"))
    }

    if (criteria.sortExpresssion) {
      const sortTokens = criteria.sortExpresssion.split(".");
      query.push(orderBy(sortTokens[0], sortTokens[1] as OrderByDirection));
    }

    if (criteria.pagination.lastItem) {
      query.push(startAfter(criteria.pagination.lastItem));
    }

    query.push(limit(criteria.pagination.pageSize));

    const data = from(this.db.getCollectionSnapshots('users', query))

    return data.pipe(
      map(results => {
        const paginatedResult = {
          data: convertSnapshotsToData<SsoUser>(results),
          lastItem: results.docs[results.docs.length - 1],
          lastPage: criteria.pagination.pageSize > results.docs.length
        } as PaginatedResult<SsoUser>;

        return paginatedResult;
      })
    )
  }

  public async searchUsersTypesense(criteria: SearchUserCriteria) {
    const adminConfig = await firstValueFrom(this.configService.getAdminConfig());
    let search = {
      q: criteria.searchTerm,
      query_by: 'email,firstname,lastname,_id',
      sort_by: criteria.sortExpresssion,
      per_page: criteria.pagination.pageSize,
      page: criteria.pagination.currentPage + 1
    }

    const filter_by = this.composeFilterByClause(criteria);
    if (filter_by) {
      search["filter_by"] = filter_by;
    }
    const typesenseClient = await this.getTypesenseClient();
    let searchResults = await typesenseClient.collections(adminConfig.typesense.usersSearch.collectionName)
      .documents()
      .search(search);

    return searchResults;
  }

  private composeFilterByClause(criteria: SearchUserCriteria): string {
    let filterBy = "";

    if (criteria.isSsoAdmin !== "") {
      filterBy = this.appendFilterByCondition(filterBy, "isSsoAdmin:=" + criteria.isSsoAdmin);
    }

    if (criteria.disabled !== "") {
      filterBy = this.appendFilterByCondition(filterBy, "disabled:=" + criteria.disabled);
    }

    if (criteria.emailVerified !== "") {
      filterBy = this.appendFilterByCondition(filterBy, "emailVerified:=" + criteria.emailVerified);
    }

    if (criteria.clientId && criteria.roleName) {
      filterBy = this.appendFilterByCondition(filterBy, "typesenseSearchTokens:=" + this.searchPrefix.clientIdRole + criteria.clientId + "_" + criteria.roleName);
    }
    else if (criteria.clientId) {
      filterBy = this.appendFilterByCondition(filterBy, "typesenseSearchTokens:=" + this.searchPrefix.clientId + criteria.clientId);
    }
    else if (criteria.roleName) {
      filterBy = this.appendFilterByCondition(filterBy, "typesenseSearchTokens:=" + this.searchPrefix.role + criteria.roleName);
    }

    return filterBy;
  }

  appendFilterByCondition(filterBy: string, condition: string): string {
    if (filterBy !== "") {
      filterBy += " && ";
    }

    filterBy += condition;

    return filterBy;
  }


  private formatSearchToken(
    freeTextToken: string,
    clientToken: string,
    roleToken: string,
    clientRoleToken: string): string {

    return `${freeTextToken} ${clientToken ? this.searchPrefix.clientId + clientToken : ""} ${roleToken ? this.searchPrefix.role + roleToken : ""} ${clientRoleToken ? this.searchPrefix.clientIdRole + clientRoleToken : ""}`.toLowerCase();
  }

  getUserById(id: string): Observable<SsoUser> {
    return from(this.db.getDocumentData<SsoUser>('users', id));
  }

  getUserByEmail(email: string): Observable<SsoUser | null> {
    email = email.toLowerCase();
    return from(this.db.getCollectionData<SsoUser[]>('users', [where("email", "==", email)]))
      .pipe(
        map(snaps => {
          let user: SsoUser | null = null;
          if (snaps.length == 1) {
            user = snaps[0];
          }
          return user;
        })
      )
  }

  async updateUser(user: SsoUser) {
    user.email = user.email.toLowerCase();
    await this.db.updateDocument('users', user.id, user);
  }

  async createUser(user: SsoUser) {
    if (user.emailVerified) {
      this.setEmailVerifiedAt(user);
    }
    if (!user.createdAt) {
      user.createdAt = this.db.getServerTimestamp();
    }

    user.email = user.email.toLowerCase();

    await this.db.setDocument('users', user.id, user);
  }

  async deleteUser(user: SsoUser) {
    return this.db.deleteDocument('users', user.id);
  }

  createAuthUser(user: any) {
    return this.http.post(formatApiUrl('/api/v1/admin/createAuthUser'), user)
      .pipe(
        catchError(this.logErrorAndThrow)
      )
  }

  deleteAuthUser(user: SsoUser) {
    return this.http.post(formatApiUrl('/api/v1/admin/deleteAuthUser'), user)
      .pipe(
        catchError(this.logErrorAndThrow)
      )
  }

  setEmailVerifiedAt(dbUser: SsoUser) {
    dbUser.emailVerifiedAt = this.db.getServerTimestamp();
  }

  sendPasswordResetEmail(userId: string, clientId: string): Observable<SsoApiResponse<SimpleMessage>> {
    return this.http.post<SsoApiResponse<SimpleMessage>>(formatApiUrl('/api/v1/admin/sendPasswordResetEmail'), { userId, clientId })
      .pipe(
        catchError(this.logErrorAndThrow)
      )
  }

  sendPasswordResetEmailFromLogin(email: string, clientId: string): Observable<SsoApiResponse<SimpleMessage>> {
    return this.http.post<SsoApiResponse<SimpleMessage>>(formatApiUrl('/api/v1/profile/passwordReset'), { email, clientId })
      .pipe(
        catchError(this.logErrorAndThrow)
      )
  }

  sendVerifyEmail(userId: string, clientId: string): Observable<SsoApiResponse<SimpleMessage>> {
    return this.http.post<SsoApiResponse<SimpleMessage>>(formatApiUrl('/api/v1/admin/sendVerifyEmail'), { userId, clientId })
      .pipe(
        catchError(this.logErrorAndThrow)
      )
  }

  sendVerifyEmailProfile(userId: string, clientId: string): Observable<SsoApiResponse<SimpleMessage>> {
    return this.http.post<SsoApiResponse<SimpleMessage>>(formatApiUrl('/api/v1/profile/sendVerifyEmail'), { userId, clientId })
      .pipe(
        catchError(this.logErrorAndThrow)
      )
  }

  changePassword(userId: string, newPassword: string): Observable<SsoApiResponse<SimpleMessage>> {
    return this.http.post<SsoApiResponse<SimpleMessage>>(formatApiUrl('/api/v1/admin/setPassword'), { userId, newPassword })
      .pipe(
        catchError(this.logErrorAndThrow)
      )
  }

  async isEmailFree(newEmail: string): Promise<boolean> {
    let isEmailFree = false;

    const existingUser = await firstValueFrom(this.getUserByEmail(newEmail));
    if (existingUser) {
      isEmailFree = false;
    } else {
      isEmailFree = true
    }

    return isEmailFree;
  }

  getUserProfile(): Observable<SsoApiResponse<UserProfile>> {
    return this.http.get<SsoApiResponse<UserProfile>>(formatApiUrl('/api/v1/profile'));
  }

  updateUserProfileNames(firstname: string, lastname: string): Observable<SsoApiResponse<UserProfile>> {
    const request = {
      firstname,
      lastname
    }

    return this.http.post<SsoApiResponse<UserProfile>>(formatApiUrl('/api/v1/profile/names'), request);
  }

  updateUserProfilePassword(email: string, currentPassword: string, newPassword: string): Observable<SsoApiResponse<UserProfile>> {
    const request = {
      currentPassword,
      newPassword
    }

    return this.http.post<SsoApiResponse<UserProfile>>(formatApiUrl('/api/v1/profile/password'), request)
      .pipe(
        tap(response => {
          signInWithEmailAndPassword(this.fireAuth, email, newPassword)
        })
      );
  }

  updateUserProfileEmail(email: string, clientId: string): Observable<SsoApiResponse<UserProfile>> {
    const request = {
      email,
      clientId
    }

    return this.http.post<SsoApiResponse<UserProfile>>(formatApiUrl('/api/v1/profile/email'), request);
  }

  verifyEmail(token: string): Observable<SsoApiResponse<UserApp[]>> {

    return this.http.get<SsoApiResponse<UserApp[]>>(formatApiUrl('/api/v1/profile/verifyEmail?token=' + token));
  }

  getApps(statusFilter: string): Observable<SsoApiResponse<UserApp[]>> {
    let url = '/api/v1/profile/apps';
    if(statusFilter){
      url += `?status=${statusFilter}`;
    }

    return this.http.get<SsoApiResponse<UserApp[]>>(formatApiUrl(url));
  }

  createSessionCookie(clientId: string) {
    return this.http.post<SsoApiResponse<any>>(formatApiUrl('/api/v1/sso/login'), { clientId });
  }

  checkSession() {
    return this.http.get<SsoApiResponse<any>>(formatApiUrl('/api/v1/sso/me'), {
      withCredentials: true
    });
  }

  deleteSessionCookie() {
    return this.http.post<SsoApiResponse<any>>(formatApiUrl('/api/v1/sso/logout'), {}, {
      withCredentials: true
    });
  }

  private logErrorAndThrow(err: any) {
    console.log(err);

    return throwError(err);
  }

  getNotificationSettings(): Observable<SsoApiResponse<NotificationSettingsDto>> {
    return this.http.get<SsoApiResponse<NotificationSettingsDto>>(formatApiUrl('/api/v1/profile/notificationSettings'));
  }

  getNotificationSettingsAdmin(userId: string): Observable<SsoApiResponse<NotificationSettingsDto>> {
    return this.http.get<SsoApiResponse<NotificationSettingsDto>>(formatApiUrl(`/api/v1/admin/notificationSettings/${userId}`));
  }

  saveNotificationSettings(settings: { notificationsEnabled: boolean, clients: Omit<NotificationClient, 'name' | 'imageURL' | 'isActive' | 'isEmailTemplatesv2Enabled'>[]}) {
    return this.http.post(formatApiUrl('/api/v1/profile/notificationSettings'), { ...settings });
  }
}
